import dayjs from 'dayjs';

import { Fragment, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import Alert from 'components/global/Alert';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import { DCLogo } from 'components/global/ProductImage';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import * as S from './TrackingInformations.styled';

const TimeStatus = ({ title, tooltip, description }) => {
	return (
		<S.TimeStatus>
			<S.TimeStatusHeader>
				<Typography color="var(--content-3)" variant="p3">
					{title}
				</Typography>
				<Tooltip placement="right" title={tooltip}>
					<Icon icon={faCircleInfo} />
				</Tooltip>
			</S.TimeStatusHeader>
			<Typography fontWeight="500" variant="p3">
				{description}
			</Typography>
		</S.TimeStatus>
	);
};

const ShipmentStatus = ({ title, date, src, isActive, expectedDate }) => {
	const { getLocaleDate } = useLocale();

	return (
		<S.ShipmentStatus className={!isActive ? 'filter' : ''}>
			<Image url={src} width="6rem" height="6rem" />
			<S.ShipmentStatusText>
				<Typography fontWeight={isActive ? '500' : '400'} variant="p3">
					{title}
				</Typography>
				{date && (
					<>
						<Typography color="var(--content-3)" variant="p3">
							{getLocaleDate(date)}
						</Typography>
					</>
				)}
				{expectedDate && (
					<S.ShipmentStatusDateWrapper>
						<Typography color="var(--content-3)" variant="span">
							<Trans
								i18nKey={'exp'}
								values={{
									date: getLocaleDate(expectedDate)
								}}
							/>
						</Typography>
					</S.ShipmentStatusDateWrapper>
				)}
			</S.ShipmentStatusText>
		</S.ShipmentStatus>
	);
};

const AccordionWrapper = ({ title, children }) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const accordionRef = useRef(null);
	return isMobile ? (
		<S.AccordionWrapper>
			<S.AccordionHeader ref={accordionRef}>
				<Typography variant="p1" fontWeight="700">
					{title}
				</Typography>
				<Icon icon={faAngleDown} size="small" />
			</S.AccordionHeader>
			<Collapse buttonRef={accordionRef}>{children}</Collapse>
		</S.AccordionWrapper>
	) : (
		<>{children}</>
	);
};

const TrackingInformations = ({ data, shipmentID }) => {
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [submitted, setSubmitted] = useState(false);
	const { getLocaleDate, getLocaleDateWithTime } = useLocale();

	const sendHelpDeskTicket = async () => {
		try {
			await axios.post(ORDER_ACTIONS.CREATE_HELPDESK_TICKET, {
				shipmentID,
				delayType: data.shipmentDelay.delayType
			});
			showSuccess();
			setSubmitted(true);
		} catch (error) {
			showError({
				message: error?.response.data.code || error?.message
			});
			AppLogger.error(error);
		}
	};

	const formatDate = (dateString) => {
		const formattedDate = dayjs(dateString);
		return formattedDate.utc().hour() === 0 && formattedDate.utc().minute() === 0
			? getLocaleDate(dateString)
			: getLocaleDateWithTime(dateString);
	};

	return (
		<S.TrackingInformation>
			{data && data.shipmentDelay && (
				<Alert
					header={data.shipmentDelay.delayType === 'pickup' ? 'pickupDelayTitle' : 'shipmentDelay'}
					ctaButtonLabel={
						data.shipmentDelay && data.shipmentDelay.isTicketSubmitted === false && !submitted
							? 'submitTicket'
							: ''
					}
					content={data.shipmentDelay.delayType === 'pickup' ? 'pickupDelayText' : 'shipmentDelayText'}
					onCtaButtonClick={sendHelpDeskTicket}
					variant="warning"
					showIcon={true}>
					{(data.shipmentDelay && data.shipmentDelay.isTicketSubmitted) || submitted ? (
						<Typography className="ticketInProgress" color="var(--blue)" fontWeight="500" variant="p">
							inprogress
						</Typography>
					) : null}
				</Alert>
			)}

			{data.timeStatsDetails && (
				<AccordionWrapper title={'expectedDelivery'}>
					<S.TimeStatusWrapper>
						<S.DCWrapper>
							<DCLogo
								data={{
									deliveryCompanyLogo: data.timeStatsDetails.deliveryCompanyLogo
								}}
							/>
							<S.DCText>
								<Typography fontWeight="500" variant="p3">
									{data.timeStatsDetails.deliveryCompanyName}
								</Typography>
								<Typography
									onClick={() =>
										window.open(data.timeStatsDetails.trackingUrl, '_blank', 'noopener,noreferrer')
									}
									color="var(--blue)"
									fontWeight="700"
									variant="p3">
									{`(${data.timeStatsDetails.trackingNumber})`}
								</Typography>
							</S.DCText>
						</S.DCWrapper>
						<TimeStatus
							title={'expectedPickupDate'}
							tooltip={'expectedPickupDateDescription'}
							description={formatDate(data.timeStatsDetails.expectedPickupDate)}
						/>
						<TimeStatus
							title={'expectedDeliveryDate'}
							tooltip={'expectedDeliveryDateDescription'}
							description={formatDate(data.timeStatsDetails.expectedDeliveryDate)}
						/>
						<TimeStatus
							title={'pickupLocation'}
							tooltip={'pickupLocationDescription'}
							description={data.timeStatsDetails.pickupLocation}
						/>
						<TimeStatus
							title={'yourAddress'}
							tooltip={'deliveryAddressDescription'}
							description={data.timeStatsDetails.deliveryAddress}
						/>
					</S.TimeStatusWrapper>
				</AccordionWrapper>
			)}
			<S.Line />
			<AccordionWrapper title={'trackingProgress'}>
				<S.ShipmentStatusWrapper>
					{data.progressBarStatusList.map((item, index) => (
						<Fragment key={index}>
							<ShipmentStatus
								src={item.logo}
								title={item.status}
								date={item.date}
								isActive={item.active}
								expectedDate={item.expectedDate}
							/>
							{index < data.progressBarStatusList.length - 1 && (
								<S.DashedLine className={!item.active ? 'filter' : ''} />
							)}
						</Fragment>
					))}
				</S.ShipmentStatusWrapper>
			</AccordionWrapper>

			<S.Line className="bottomLine" />
		</S.TrackingInformation>
	);
};
export default TrackingInformations;
